import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X, ArrowRight, Sparkles, FileText } from 'lucide-react';
import { PollarLogoSVG } from '@/constants/logo';
import { trackPageView, trackClick, EventTypes } from '@/utils/analytics';

const AlternativesComparison = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('alternatives-comparison');
  }, []);

  const handleCTA = (location) => {
    trackClick(`alternatives-cta-${location}`, window.location.href);
    navigate('/login?signup=true');
  };

  const comparisonRows = [
    {
      feature: 'Output',
      standard: 'Raw Word Clouds & CSVs',
      pollar: 'AI-Prioritized Action Plans',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Analysis Workflow',
      standard: 'Manual (You sort the spreadsheet)',
      pollar: 'Automated (AI clusters insights instantly)',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Usage',
      standard: 'Fragmented (Live OR Async)',
      pollar: 'Unified (Live Workshops + Async Surveys)',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Cost',
      standard: 'Expensive Per-User Pricing',
      pollar: 'Simple Flat Rate',
      standardIcon: false,
      pollarIcon: true
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pollar vs. Mentimeter/SurveyMonkey - The AI-Powered Alternative</title>
        <meta 
          name="description" 
          content="Stop paying for word clouds. Pollar Ideation organizes your workshop feedback into actionable insights using AI. Compare features and pricing here." 
        />
        <meta property="og:title" content="Pollar vs. Mentimeter/SurveyMonkey - The AI-Powered Alternative" />
        <meta 
          property="og:description" 
          content="Stop paying for word clouds. Pollar Ideation organizes your workshop feedback into actionable insights using AI." 
        />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL}/alternatives/mentimeter-vs-pollar`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <PollarLogoSVG className="w-10 h-10" />
                <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Pollar
                </span>
              </button>
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => handleCTA('nav')}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  Get Started Free
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Section A: Hero */}
        <section className="pt-20 pb-16 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Alternative to Mentimeter & SurveyMonkey
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              The Smarter, Faster Alternative to{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Mentimeter & SurveyMonkey
              </span>
            </h1>

            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
                Stop Paying for Raw Data. Pay for Answers.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Mentimeter and SurveyMonkey just collect the dots. Pollar connects them. 
                Get AI-organized insights for a fraction of the cost.
              </p>
            </div>

            <Button 
              size="lg"
              onClick={() => handleCTA('hero')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg px-8 py-6 h-auto"
            >
              Switch to Pollar for Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>

            <p className="text-sm text-gray-500 mt-4">
              No credit card required • Free plan available • Setup in 2 minutes
            </p>
          </div>
        </section>

        {/* Section B: Comparison Table */}
        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Feature Comparison</h2>
              <p className="text-xl text-gray-600">
                See how Pollar delivers more value with less work
              </p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-6 px-6 text-left bg-gray-50 font-bold text-lg">
                      Feature
                    </th>
                    <th className="py-6 px-6 text-center bg-gray-100 text-gray-500 font-semibold">
                      Standard Tools
                      <div className="text-sm font-normal mt-1">(Mentimeter/SurveyMonkey)</div>
                    </th>
                    <th className="py-6 px-6 text-center bg-gradient-to-r from-indigo-50 to-purple-50 font-bold text-lg">
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        Pollar Ideation
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-6 px-6 font-semibold text-gray-900">
                        {row.feature}
                      </td>
                      <td className="py-6 px-6 bg-gray-50">
                        <div className="flex items-center justify-center gap-3 text-gray-600">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-center">{row.standard}</span>
                        </div>
                      </td>
                      <td className="py-6 px-6 bg-indigo-50/30">
                        <div className="flex items-center justify-center gap-3 text-gray-900 font-medium">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-center">{row.pollar}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-6">
              {comparisonRows.map((row, index) => (
                <Card key={index} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-4 text-gray-900">{row.feature}</h3>
                    
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-gray-500 mb-1">Standard Tools</div>
                            <div className="text-gray-600">{row.standard}</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-indigo-700 mb-1">Pollar</div>
                            <div className="text-gray-900 font-medium">{row.pollar}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Section C: The Tangible Difference */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                Why "Actionable Insights" Matter
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Standard tools leave you with a mess of sticky notes to organize after the meeting ends. 
                Pollar's AI clusters duplicates, identifies themes, and drafts a report before you even leave the room.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* The Old Way */}
              <Card className="border-2 border-gray-300 relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                  The Old Way
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-gray-500">What they give you</h3>
                  
                  {/* Word Cloud Simulation */}
                  <div className="bg-gray-100 rounded-lg p-6 min-h-[300px] flex items-center justify-center">
                    <div className="text-center space-y-2">
                      <div className="flex flex-wrap justify-center gap-2 mb-4">
                        <span className="text-2xl text-gray-400">collaboration</span>
                        <span className="text-4xl text-gray-500">feedback</span>
                        <span className="text-xl text-gray-400">improvement</span>
                      </div>
                      <div className="flex flex-wrap justify-center gap-2 mb-4">
                        <span className="text-3xl text-gray-500">process</span>
                        <span className="text-xl text-gray-400">communication</span>
                        <span className="text-2xl text-gray-400">team</span>
                      </div>
                      <div className="flex flex-wrap justify-center gap-2">
                        <span className="text-xl text-gray-400">workflow</span>
                        <span className="text-3xl text-gray-500">efficiency</span>
                        <span className="text-2xl text-gray-400">tools</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Requires manual sorting</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Hours of post-processing</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>No actionable structure</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* The Pollar Way */}
              <Card className="border-2 border-indigo-500 relative overflow-hidden shadow-xl">
                <div className="absolute top-4 right-4 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  The Pollar Way
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    What we give you
                  </h3>
                  
                  {/* Organized List */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 min-h-[300px]">
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-700 font-bold text-sm">1</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Improve Team Communication</h4>
                            <p className="text-sm text-gray-600">7 related suggestions</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-700 font-bold text-sm">2</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Streamline Workflows</h4>
                            <p className="text-sm text-gray-600">5 related suggestions</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-700 font-bold text-sm">3</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Enhance Collaboration Tools</h4>
                            <p className="text-sm text-gray-600">4 related suggestions</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Instantly organized</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Ready-to-use action items</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Themes identified automatically</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section D: Final CTA */}
        <section className="py-20 px-6 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to upgrade your insights?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Join facilitators who've switched from word clouds to actionable insights. 
              Start free, no credit card required.
            </p>
            <Button 
              size="lg"
              onClick={() => handleCTA('final')}
              className="bg-white text-indigo-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto font-semibold"
            >
              Start Your First Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <div className="mt-8 flex items-center justify-center gap-8 text-indigo-100">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Free plan available</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>No credit card needed</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>2-minute setup</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-400 py-12 px-6">
          <div className="container mx-auto max-w-6xl text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <PollarLogoSVG className="w-8 h-8" />
              <span className="text-xl font-bold text-white">Pollar</span>
            </div>
            <p className="mb-4">
              The AI-powered alternative to traditional feedback tools
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <button onClick={() => navigate('/')} className="hover:text-white transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                Sign In
              </button>
              <button onClick={() => handleCTA('footer')} className="hover:text-white transition-colors">
                Get Started
              </button>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-sm">
              © {new Date().getFullYear()} Pollar. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AlternativesComparison;
